import React, { useEffect } from "react";
import { Typography, Card, Space, Row, Col } from "antd";
import {
  SafetyOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  TeamOutlined,
  WarningOutlined,
  FileTextOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "../Home/LanguageContext";
import { PublicPageLayout } from "../../Layout/PublicPageLayout";
import DeleteAccountSection from "./DeleteAccountSection";

const { Paragraph } = Typography;

const SafetyPageContent: React.FC = () => {
  const { t } = useLanguage();

  useEffect(() => {
    document.title = "Safety & Security - Lingua Talk";
  }, []);

  useEffect(() => {
    if (window.location.hash === "#delete-account") {
      const el = document.getElementById("delete-account");
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Safety & Security — LinguaTalk</title>
        <meta name="description" content="LinguaTalk safety policies, moderation, and account deletion." />
      </Helmet>
      <main className="public-main">
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <header className="public-hero public-hero-green">
            <SafetyOutlined className="public-hero-icon" />
            <h1 className="public-hero-title">{t("safetyTitle")}</h1>
            <p className="public-hero-desc">{t("safetyIntro")}</p>
          </header>

          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <Card
                className="public-card"
                title={
                  <Space>
                    <ExclamationCircleOutlined />
                    <span>{t("zeroToleranceTitle")}</span>
                  </Space>
                }
              >
                <Paragraph>{t("zeroToleranceIntro")}</Paragraph>
                <ul className="public-list">
                  <li>{t("zeroToleranceItem1")}</li>
                  <li>{t("zeroToleranceItem2")}</li>
                  <li>{t("zeroToleranceItem3")}</li>
                </ul>
                <Paragraph style={{ fontWeight: 700, color: "#f87171" }}>
                  {t("zeroToleranceAction")}
                </Paragraph>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                className="public-card"
                title={
                  <Space>
                    <EyeOutlined />
                    <span>{t("moderationTitle")}</span>
                  </Space>
                }
              >
                <Paragraph>{t("zeroToleranceIntro")}</Paragraph>
                <ul className="public-list">
                  <li>{t("moderationItem1")}</li>
                  <li>{t("moderationItem2")}</li>
                  <li>{t("moderationItem3")}</li>
                </ul>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                className="public-card"
                title={
                  <Space>
                    <TeamOutlined />
                    <span>{t("userAgePolicyTitle")}</span>
                  </Space>
                }
              >
                <Paragraph>{t("userAgePolicyDesc")}</Paragraph>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                className="public-card"
                title={
                  <Space>
                    <WarningOutlined />
                    <span>{t("reportAbuseTitle")}</span>
                  </Space>
                }
              >
                <Paragraph>{t("reportAbuseIntro")}</Paragraph>
                <ul className="public-list">
                  <li>{t("reportAbuseItem1")}</li>
                  <li>{t("reportAbuseItem2")}</li>
                </ul>
                <Paragraph>{t("reportAbuseUseButton")}</Paragraph>
              </Card>
            </Col>
          </Row>

          <Card
            className="public-card"
            title={
              <Space>
                <FileTextOutlined />
                <span>{t("complianceTitle")}</span>
              </Space>
            }
          >
            <Paragraph>{t("compliesWith")}</Paragraph>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <div className="public-stat-block">
                  <Paragraph>{t("complianceItem1")}</Paragraph>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div className="public-stat-block">
                  <Paragraph>{t("complianceItem2")}</Paragraph>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div className="public-stat-block">
                  <Paragraph>{t("complianceItem3")}</Paragraph>
                </div>
              </Col>
            </Row>
          </Card>

          <Card
            className="public-card public-card-warm"
            title={
              <Space>
                <MailOutlined />
                <span>{t("contactTitle")}</span>
              </Space>
            }
          >
            <div className="public-center">
              <Paragraph>{t("safetyContactIntro")}</Paragraph>
              <Paragraph>
                <a className="public-link" href="mailto:support@linguatalk.com">
                  support@linguatalk.com
                </a>
              </Paragraph>
            </div>
          </Card>

          <DeleteAccountSection />
        </Space>
      </main>
    </>
  );
};

const SafetyPage: React.FC = () => (
  <PublicPageLayout>
    <SafetyPageContent />
  </PublicPageLayout>
);

export default SafetyPage;
